import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Box,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  ShoppingCart,
  AccountCircle,
  Menu as MenuIcon,
  Close,
  LocalShipping,
  Phone,
  Email,
  Home,
  MedicalServices,
  Spa,
  LocationOn,
  Login,
  Logout,
  Person,
  Book,
  Reorder,
  LocalPharmacy,
  CardMembership,
  Warning,
  CheckCircle,
  Error,
  LocalOffer,
  Category,
} from '@mui/icons-material';

// React Icons for Beauty & Wellness categories
import { IoLeafSharp } from "react-icons/io5";
import { LuBaby } from "react-icons/lu";
import { MdOutlineMasks } from "react-icons/md";
import { BiSolidInjection } from "react-icons/bi";
import { GiStomach } from "react-icons/gi";
import { CiStethoscope } from "react-icons/ci";
import { MdElderlyWoman } from "react-icons/md";
import { RiDrinksFill } from "react-icons/ri";
import { MdOutlineDeviceThermostat } from "react-icons/md";
import { FaHeart } from "react-icons/fa";
import { FaTooth } from "react-icons/fa";
import { MdOutlineWash } from "react-icons/md";
import { FaWeightScale } from "react-icons/fa6";
import { IoWoman } from "react-icons/io5";
import { GiHealthNormal } from "react-icons/gi";
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useUserStore from '../../stores/userStore';
import searchService from '../../services/searchService';
import userService from '../../services/userService';
import OtcToolbar from './OtcToolbar';
import LogoutButton from '../auth/LogoutButton';

const AppBarMain = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { cartCount, loginFlag, setLoginFlag, setCartCount, logout } = useUserStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [beautyMenuOpen, setBeautyMenuOpen] = useState(false);
  const [pincode, setPincode] = useState('');
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState(null); // 'available', 'unavailable', null
  const [pincodeMessage, setPincodeMessage] = useState('');
  const [showPincodeDialog, setShowPincodeDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Valid pincodes array (same as Vue version)
  const validPincodes = [
    500030, 500004, 500045, 500007, 500012, 500015, 500044, 500013, 500004,
    501201, 500015, 500044, 501301, 501301, 501301, 500030, 500040, 500020,
    500048, 500015, 500058, 500064, 500005, 500034, 500027, 500004, 500012,
    500016, 500003, 500018, 500080, 500039, 501301, 500013, 500022, 500024,
    500064, 501301, 501301, 500005, 500081, 500015, 500008, 500024, 500028,
    500006, 500060, 500062, 500062, 500018, 500053, 500065, 500018, 500029,
    500015, 500001, 500080, 501301, 500015, 500008, 500020, 500015, 500068,
    500062, 500052, 500066, 500029, 500025, 500001, 500051, 500035, 500028,
    500064, 500016, 500016, 500008, 500002, 500013, 500001, 500048, 500076,
    500082, 500007, 500031, 500040, 500015, 500007, 500015, 500079, 500085,
    500033, 500008, 500058, 500079, 500006, 501301, 500077, 501301, 501301,
    500005, 500004, 500062, 500003, 500064, 500030, 500015, 501301, 500067,
    500015, 500015, 500062, 500074, 500063, 500015, 500017, 500017, 500008,
    500003, 500081, 500036, 500076, 500005, 500006, 500068, 500001, 500002,
    500018, 500040, 500015, 500028, 500020, 500020, 500044, 500008, 500062,
    500029, 500026, 500060, 500063, 500044, 500007, 500027, 500015, 500036,
    500029, 500012, 500036, 500060, 500025, 500004, 500005, 500004, 500015,
    500039, 500003, 500015, 500016, 501301, 500095, 500065, 500069, 500045,
    500071, 500041, 500016, 500030, 500035, 500029, 500095, 501301, 500074,
    500015, 500023, 501301, 500024, 500059, 500059, 500017, 500008, 500018,
    500018, 500038, 500059, 500035, 500003, 500001, 500013, 500065, 500028,
    500052, 500004, 500061, 500076, 500082, 500033, 500073, 500013, 500036,
    500001, 500095, 500027, 500065, 500015, 500039, 500052, 500018, 500064,
    500002, 500007, 501301, 500015, 500008, 500080, 500039, 500053, 500070,
    500079, 500070, 500038, 500004, 500044, 500057, 501101, 501301, 500023,
    500045, 500020, 500013,
  ];

  // Function to get category icon
  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'Ayurveda': IoLeafSharp,
      'Baby Care': LuBaby,
      'Covid Essentials': MdOutlineMasks,
      'Diabetes Care': BiSolidInjection,
      'Digestive Health': GiStomach,
      "Doctor's Corner": CiStethoscope,
      'Elderly Care': MdElderlyWoman,
      'Health Food & Drinks': RiDrinksFill,
      'Healthcare Devices': MdOutlineDeviceThermostat,
      'Heart Health': FaHeart,
      'Oral Care': FaTooth,
      'Sanitizers & Handwash': MdOutlineWash,
      'Weight Management': FaWeightScale,
      'Women Care': IoWoman,
    };
    
    return iconMap[categoryName] || GiHealthNormal;
  };

  const menuItems = [
    {
      icon: <Person />,
      title: "Profile",
      click: () => navigate('/profile'),
    },
    {
      icon: <Book />,
      title: "Address Book",
      click: () => navigate('/addressbook'),
    },
    {
      icon: <Reorder />,
      title: "Orders List",
      click: () => navigate('/orderslist'),
    },
    {
      icon: <LocalPharmacy />,
      title: "Prescriptions",
      click: () => navigate('/prescriptions'),
    },
    {
      icon: <CardMembership />,
      title: "Membership",
      click: () => navigate('/membership'),
    },
    {
      icon: <Logout />,
      title: "Logout",
      click: () => {
        logout();
        navigate('/');
      },
    },
  ];

  useEffect(() => {
    getCartCount();
    getCategories();
    
    // Add click outside listener
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getCartCount = async () => {
    try {
      setCartCount(); // This should dispatch the cart count action
    } catch (error) {
      console.error('Error getting cart count:', error);
    }
  };

  const getCategories = async () => {
    try {
      const result = await userService.getCategories();
      const categoriesResponse = result.data || result || [];
      // Handle new object format with id and name properties
      const categories = categoriesResponse.map(category => ({
        id: category.id,
        name: category.name
      }));
      setCategories(categories);
    } catch (error) {
      console.error('Error getting categories:', error);
    }
  };

  const debouncedSearch = (searchTerm) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setSearchLoading(true);
      try {
        const suggestions = await searchService.getSearchSuggestions(searchTerm);
        setSearchSuggestions(suggestions);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Error fetching search suggestions:", error);
        setSearchSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchTerm.trim() && searchTerm.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setShowSuggestions(false);
    
    if (suggestion.type === 'product') {
      navigate(`/product/${suggestion.id}`);
    } else {
      navigate(`/otc/${suggestion.id}`);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCartClick = () => {
    navigate('/cart');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handlePincodeInput = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 6);
    setPincode(value);
    // Reset status when user types
    if (pincodeStatus) {
      setPincodeStatus(null);
      setPincodeMessage('');
    }
  };

  const checkDelivery = async () => {
    if (!pincode || pincode.length !== 6) {
      alert('Please enter a valid 6-digit pincode');
      return;
    }

    try {
      setPincodeLoading(true);
      const response = await userService.checkPincode(pincode);
      
      if (response.data.is_active) {
        setPincodeStatus('available');
        setPincodeMessage(response.data.message);
      } else {
        setPincodeStatus('unavailable');
        setPincodeMessage(response.data.message);
        setShowPincodeDialog(true);
      }
    } catch (error) {
      console.error("Error checking pincode:", error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        if (errorData.pincode) {
          setPincodeStatus('unavailable');
          setPincodeMessage(`Validation Error: ${errorData.pincode.join(', ')}`);
        } else {
          setPincodeStatus('unavailable');
          setPincodeMessage("Invalid pincode format. Please enter a 6-digit pincode.");
        }
        setShowPincodeDialog(true);
      } else if (error.response && error.response.status === 404) {
        setPincodeStatus('unavailable');
        setPincodeMessage(error.response.data.message);
        setShowPincodeDialog(true);
      } else {
        setPincodeStatus('unavailable');
        setPincodeMessage("Unable to check pincode. Please try again later.");
        setShowPincodeDialog(true);
      }
    } finally {
      setPincodeLoading(false);
    }
  };

  const handleOtcPage = (item) => {
    navigate(`/otcproducts/?category=${encodeURIComponent(item)}`);
  };

  return (
    <>
      {/* Desktop & Tablet Layout */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            backgroundColor: 'rgb(255 255 255)blue'
          }}
        >
          <Toolbar 
            disableGutters
            sx={{ 
              py: { md: 1.5, lg: 2 },
              px: { md: 2, lg: 3 },
              minHeight: { md: '72px !important', lg: '80px !important' },
              flexDirection: 'column',
              backgroundColor: 'aliceblue',
              alignItems: 'stretch'
            }}
          >
            <Container maxWidth="xl" sx={{ px: { md: 2, lg: 3 } }}>
              {/* First Row: Logo, Pincode, Search, Cart, Profile */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
                backgroundColor: 'aliceblue',
                mb: { md: 1.5, lg: 2 }
              }}>
                {/* Left: Logo */}
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box 
                    component="img" 
                    src="/assets/logo_new.png" 
                    alt="PharmaMan Logo"
                    sx={{ 
                      height: { md: 44, lg: 60 },
                      width: 'auto', 
                      objectFit: 'contain',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 0.85 }
                    }}
                    onClick={() => navigate('/')}
                  />
                  <Typography 
                    sx={{ 
                      fontWeight: 'bold',
                      fontFamily: 'Lexend Peta',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 0.85 }
                    }}
                    onClick={() => navigate('/')}
                  >
                    PHARMA MAN
                  </Typography>
                </Box>

                {/* Right: Pincode, Search, Cart, Profile */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { md: 1.5, lg: 2 },
                  flexShrink: 0
                }}>
                  {/* Pincode Section */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Deliver to</Typography>

                    <TextField
                      value={pincode}
                      onChange={handlePincodeInput}
                      placeholder="Pincode"
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: pincodeStatus && (
                          <InputAdornment position="end">
                            {pincodeStatus === 'available' ? (
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            ) : (
                              <Error sx={{ color: 'error.main', fontSize: 20 }} />
                            )}
                          </InputAdornment>
                        ),
                      }}
                      sx={{ 
                        width: { md: 140, lg: 160 },
                        '& .MuiOutlinedInput-input': {
                          py: 1.25,
                          fontSize: '0.875rem',
                          letterSpacing: '0.05em',
                          fontWeight: 500
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          '&.Mui-focused fieldset': {
                            borderWidth: 1.5,
                            borderColor: pincodeStatus === 'available' ? 'success.main' : 
                                       pincodeStatus === 'unavailable' ? 'error.main' : 
                                       theme.palette.primary.main,
                          },
                          '& fieldset': {
                            borderColor: pincodeStatus === 'available' ? 'success.main' : 
                                       pincodeStatus === 'unavailable' ? 'error.main' : 
                                       'rgba(0, 0, 0, 0.23)',
                          },
                        },
                      }}
                      disabled={pincodeLoading}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={checkDelivery}
                      disabled={!pincode || pincode.length !== 6 || pincodeLoading}
                      sx={{ 
                        minWidth: { md: 70, lg: 80 },
                        px: 2,
                        py: 1.25,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        boxShadow: 'none',
                        '&:hover': { 
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                        }
                      }}
                    >
                      {pincodeLoading ? '...' : 'Check'}
                    </Button>
                  </Box>

                  {/* Search Section */}
                  <Box sx={{ position: 'relative', width: { md: 280, lg: 320 } }} ref={searchRef}>
                    <form onSubmit={handleSearchSubmit}>
                      <TextField
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search medicines & products"
                        size="small"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ color: theme.palette.text.secondary, fontSize: 22 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-input': {
                            py: 1.25,
                            fontSize: '0.875rem',
                          },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`,
                            },
                          },
                        }}
                        onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      />
                    </form>

                    {/* Search Suggestions */}
                    {showSuggestions && (
                      <Card
                        elevation={8}
                        sx={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          maxHeight: 400,
                          overflow: 'auto',
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                        }}
                      >
                        {searchLoading ? (
                          <Box sx={{ textAlign: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                              Searching...
                            </Typography>
                          </Box>
                        ) : searchSuggestions.length > 0 ? (
                          <List sx={{ py: 0.5 }}>
                            {searchSuggestions.map((suggestion) => (
                              <ListItem
                                key={suggestion.id}
                                onClick={() => handleSuggestionClick(suggestion)}
                                sx={{ 
                                  cursor: 'pointer',
                                  py: 1.25,
                                  px: 2,
                                  transition: 'background-color 0.2s',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {suggestion.name}
                                    </Typography>
                                  }
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                      <Chip
                                        label={suggestion.type === 'product' ? 'Medicine' : 'OTC'}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                        }}
                                      />
                                      {suggestion.manufacturer && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                          {suggestion.manufacturer}
                                        </Typography>
                                      )}
                                      {suggestion.price && (
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: '0.75rem' }}>
                                          ₹{suggestion.price}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                                {suggestion.prescription_required && (
                                  <Warning sx={{ color: 'error.main', fontSize: 18 }} />
                                )}
                              </ListItem>
                            ))}
                          </List>
                        ) : searchTerm && searchTerm.length >= 2 ? (
                          <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              No suggestions found
                            </Typography>
                            <Button 
                              size="small" 
                              variant="contained" 
                              onClick={handleSearchSubmit}
                              sx={{ 
                                textTransform: 'none',
                                borderRadius: 1.5,
                                px: 3
                              }}
                            >
                              Search for "{searchTerm}"
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Type at least 2 characters to search
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    )}
                  </Box>

                  {/* User Section */}
                  {!loginFlag ? (
                    <Button
                      variant="outlined"
                      startIcon={<Login sx={{ fontSize: 18 }} />}
                      onClick={handleLoginClick}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        px: 2.5,
                        py: 1.25,
                        borderRadius: 1.5,
                        borderWidth: 1.5,
                        '&:hover': {
                          borderWidth: 1.5,
                        }
                      }}
                    >
                      Sign In
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {/* Cart */}
                      <Tooltip title={`Cart (${cartCount} items)`} arrow>
                        <IconButton 
                          onClick={handleCartClick}
                          sx={{ 
                            p: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <Badge badgeContent={cartCount} color="error" sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.7rem',
                              minWidth: 18,
                              height: 18,
                            }
                          }}>
                            <ShoppingCart sx={{ fontSize: 22 }} />
                          </Badge>
                        </IconButton>
                      </Tooltip>

                      {/* Profile Menu */}
                      <IconButton 
                        onClick={handleMenuClick}
                        sx={{ 
                          p: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <AccountCircle sx={{ fontSize: 28 }} />
                      </IconButton>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                          sx: {
                            mt: 1,
                            minWidth: 220,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: 'background.paper',
                            overflow: 'visible',
                          }
                        }}
                      >
                        {menuItems.map((item, index) => (
                          <MenuItem
                            key={index}
                            onClick={() => { item.click(); handleMenuClose(); }}
                            sx={{
                              py: 1.25,
                              px: 2,
                              color: item.title === 'Logout' ? 'error.main' : 'text.primary',
                              '&:hover': {
                                backgroundColor: `${theme.palette.primary.main}15`,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 40,
                                color: item.title === 'Logout' ? 'error.main' : 'primary.main',
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: item.title === 'Logout' ? 'error.main' : 'text.primary',
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Second Row: Navigation Menu - All Centered */}
              <Box sx={{ 
                backgroundColor: theme.palette.primary.main,
                pt: { md: 1.25, lg: 1.5 },
                pb: { md: 1.25, lg: 1.5 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { md: 0.5, lg: 1 },
                  flexWrap: 'wrap'
                }}>
                  <Button
                    startIcon={<Home sx={{ fontSize: 20, color: 'white' }} />}
                    onClick={() => navigate('/')}
                    sx={{ 
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Home
                  </Button>
                  
                  <Button
                    startIcon={<MedicalServices sx={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => navigate('/allmedicines')}
                    sx={{ 
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    All Medicines
                  </Button>
                  
                  <Menu
                    anchorEl={beautyMenuOpen}
                    open={Boolean(beautyMenuOpen)}
                    onClose={() => setBeautyMenuOpen(false)}
                    MenuListProps={{
                      onMouseLeave: () => setBeautyMenuOpen(false),
                    }}
                    PaperProps={{
                      sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        mt: 1,
                        overflow: 'hidden'
                      }
                    }}
                    transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                  >
                    <Box sx={{ 
                      p: 3, 
                      minWidth: { md: 600, lg: 700 },
                      backgroundColor: 'white'
                    }}>
                      {/* Header */}
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Spa sx={{ fontSize: 20 }} />
                          Beauty & Wellness Categories
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem' }}
                        >
                          Explore our wide range of health and beauty products
                        </Typography>
                      </Box>

                      {/* Categories Grid */}
                      <Grid container spacing={2}>
                        {categories.map((item, index) => {
                          const IconComponent = getCategoryIcon(item.name);
                          return (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                              <Card
                                sx={{
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease-in-out',
                                  border: '1px solid transparent',
                                  borderRadius: 2,
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: `${theme.palette.primary.main}08`
                                  }
                                }}
                                onClick={() => {
                                  handleOtcPage(item.name);
                                  setBeautyMenuOpen(false);
                                }}
                              >
                                <CardContent sx={{ 
                                  p: 2,
                                  '&:last-child': { pb: 2 }
                                }}>
                                  <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5
                                  }}>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 32,
                                      height: 32,
                                      borderRadius: 1,
                                      backgroundColor: `${theme.palette.primary.main}15`,
                                      flexShrink: 0
                                    }}>
                                      <IconComponent 
                                        style={{ 
                                          fontSize: 18,
                                          color: theme.palette.primary.main
                                        }} 
                                      />
                                    </Box>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        color: theme.palette.text.primary,
                                        lineHeight: 1.3,
                                        wordBreak: 'break-word',
                                        flex: 1
                                      }}
                                    >
                                      {item.name}
                                    </Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Menu>
                  
                  <Button
                    startIcon={<Spa sx={{ fontSize: 18, color: 'white' }} />}
                    onClick={(e) => setBeautyMenuOpen(e.currentTarget)}
                    sx={{ 
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Beauty & Wellness
                  </Button>


                  <Button
                    startIcon={<IoLeafSharp style={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => handleOtcPage('Ayurveda')}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    Ayurveda
                  </Button>

                  <Button
                    startIcon={<MdOutlineWash style={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => handleOtcPage('Skin Care')}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    Skin Care
                  </Button>

                  <Button
                    startIcon={<GiHealthNormal style={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => handleOtcPage('Vitamins & Supplements')}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    Vitamins
                  </Button>

                  <Button
                    startIcon={<LuBaby style={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => handleOtcPage('Baby Care')}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    Baby Care
                  </Button>

                  <Button
                    startIcon={<CardMembership sx={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => navigate('/membership')}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1.2rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    Membership
                  </Button>

                  {/* <Button
                    startIcon={<LocalOffer sx={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => navigate('/offers')}
                    sx={{ 
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Offers
                  </Button>

                  <Button
                    startIcon={<Category sx={{ fontSize: 18, color: 'white' }} />}
                    onClick={() => navigate('/categories')}
                    sx={{ 
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: { md: 1.5, lg: 2 },
                      py: 0.75,
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    All Categories
                  </Button> */}
                </Box>
              </Box>
            </Container>
          </Toolbar>
        </AppBar>
      </Box>

      {/* Mobile Layout */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            backgroundColor: 'white'
          }}
        >
          <Toolbar 
            disableGutters
            sx={{ 
              py: 1.5,
              px: 2,
              minHeight: '64px !important',
              flexDirection: 'column',
              alignItems: 'stretch'
            }}
          >
            <Container maxWidth="xl" sx={{ px: 2 }}>
              {/* Mobile Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                width: '100%',
                mb: 1.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box 
                    component="img" 
                    src="/assets/logo_new.png" 
                    alt="PharmaMan Logo"
                    sx={{ 
                      height: 60, 
                      width: 'auto', 
                      objectFit: 'contain',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 0.85 }
                    }}
                    onClick={() => navigate('/')}
                  />
                  <Typography 
                    sx={{ 
                      fontWeight: 'bold',
                      fontFamily: 'Lexend',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 0.85 },
                      display: { xs: 'none', sm: 'block' }
                    }}
                    onClick={() => navigate('/')}
                  >
                    PHARMA MAN
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {loginFlag && (
                    <>
                      <Tooltip title={`Cart (${cartCount} items)`} arrow>
                        <IconButton 
                          onClick={handleCartClick}
                          size="small"
                          sx={{ 
                            p: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <Badge badgeContent={cartCount} color="error" sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.7rem',
                              minWidth: 18,
                              height: 18,
                            }
                          }}>
                            <ShoppingCart sx={{ fontSize: 22 }} />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Profile" arrow>
                        <IconButton 
                          onClick={handleMenuClick}
                          size="small"
                          sx={{ 
                            p: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <AccountCircle sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {!loginFlag && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Login sx={{ fontSize: 18 }} />}
                      onClick={handleLoginClick}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        borderRadius: 1.5,
                        borderWidth: 1.5,
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        '&:hover': {
                          borderWidth: 1.5,
                        }
                      }}
                    >
                      Sign In
                    </Button>
                  )}
                  <IconButton 
                    onClick={handleMobileMenuToggle}
                    size="small"
                    sx={{ 
                      p: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <MenuIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Mobile Search Bar */}
              <Box sx={{ position: 'relative' }} ref={searchRef}>
                <form onSubmit={handleSearchSubmit}>
                  <TextField
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search medicines & products"
                    size="small"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-input': {
                        py: 1.25,
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`,
                        },
                      },
                    }}
                    onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  />
                </form>

                {/* Mobile Search Suggestions */}
                {showSuggestions && (
                  <Card
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: 300,
                      overflow: 'auto',
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    {searchLoading ? (
                      <Box sx={{ textAlign: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                          Searching...
                        </Typography>
                      </Box>
                    ) : searchSuggestions.length > 0 ? (
                      <List sx={{ py: 0.5 }}>
                        {searchSuggestions.map((suggestion) => (
                          <ListItem
                            key={suggestion.id}
                            onClick={() => handleSuggestionClick(suggestion)}
                            sx={{ 
                              cursor: 'pointer',
                              py: 1.25,
                              px: 2,
                              transition: 'background-color 0.2s',
                              '&:hover': { 
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {suggestion.name}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={suggestion.type === 'product' ? 'Medicine' : 'OTC'}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                    }}
                                  />
                                  {suggestion.manufacturer && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                      {suggestion.manufacturer}
                                    </Typography>
                                  )}
                                  {suggestion.price && (
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: '0.75rem' }}>
                                      ₹{suggestion.price}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            {suggestion.prescription_required && (
                              <Warning sx={{ color: 'error.main', fontSize: 18 }} />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    ) : searchTerm && searchTerm.length >= 2 ? (
                      <Box sx={{ textAlign: 'center', p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          No suggestions found
                        </Typography>
                        <Button 
                          size="small" 
                          variant="contained" 
                          onClick={handleSearchSubmit}
                          sx={{ 
                            textTransform: 'none',
                            borderRadius: 1.5,
                            px: 3
                          }}
                        >
                          Search for "{searchTerm}"
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', p: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Type at least 2 characters to search
                        </Typography>
                      </Box>
                    )}
                  </Card>
                )}
              </Box>

              {/* Mobile Menu */}
              <Collapse in={mobileMenuOpen}>
                <Box sx={{ 
                  mt: 2, 
                  p: 2.5, 
                  backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.06)'
                }}>
                  {/* Pincode */}
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        value={pincode}
                        onChange={handlePincodeInput}
                        placeholder="Enter 6-digit pincode"
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                            </InputAdornment>
                          ),
                          endAdornment: pincodeStatus && (
                            <InputAdornment position="end">
                              {pincodeStatus === 'available' ? (
                                <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                              ) : (
                                <Error sx={{ color: 'error.main', fontSize: 18 }} />
                              )}
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-input': {
                            py: 1,
                            fontSize: '0.875rem',
                            letterSpacing: '0.05em',
                            fontWeight: 500
                          },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            '&.Mui-focused fieldset': {
                              borderWidth: 1.5,
                              borderColor: pincodeStatus === 'available' ? 'success.main' : 
                                         pincodeStatus === 'unavailable' ? 'error.main' : 
                                         theme.palette.primary.main,
                            },
                          },
                        }}
                        disabled={pincodeLoading}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={checkDelivery}
                        disabled={!pincode || pincode.length !== 6 || pincodeLoading}
                        sx={{ 
                          minWidth: 70,
                          px: 2,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          borderRadius: 1.5,
                          boxShadow: 'none',
                          '&:hover': { 
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                      >
                        {pincodeLoading ? '...' : 'Check'}
                      </Button>
                    </Box>
                    
                    {/* Mobile Status Message */}
                    {pincodeMessage && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: pincodeStatus === 'available' ? 'success.main' : 'error.main',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          display: 'block',
                          textAlign: 'left',
                          px: 0.5
                        }}
                      >
                        {pincodeStatus === 'available' ? '✓ ' : '✗ '}{pincodeMessage}
                      </Typography>
                    )}
                  </Box>

                  {/* Navigation Links */}
                  <Box sx={{ mb: 2.5 }}>
                    <Button
                      fullWidth
                      startIcon={<Home sx={{ fontSize: 18 }} />}
                      onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                      sx={{ 
                        justifyContent: 'flex-start',
                        mb: 0.75,
                        py: 1.25,
                        px: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      Home
                    </Button>
                    <Button
                      fullWidth
                      startIcon={<MedicalServices sx={{ fontSize: 18 }} />}
                      onClick={() => { navigate('/allmedicines'); setMobileMenuOpen(false); }}
                      sx={{ 
                        justifyContent: 'flex-start',
                        mb: 0.75,
                        py: 1.25,
                        px: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      All Medicines
                    </Button>
                    <Button
                      fullWidth
                      startIcon={<Spa sx={{ fontSize: 18 }} />}
                      onClick={() => setMobileMenuOpen(false)}
                      sx={{ 
                        justifyContent: 'flex-start',
                        mb: 0.75,
                        py: 1.25,
                        px: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      Beauty & Wellness
                    </Button>

                    <Button
                      fullWidth
                      startIcon={<LocalPharmacy sx={{ fontSize: 18 }} />}
                      onClick={() => { navigate('/otcproducts/'); setMobileMenuOpen(false); }}
                      sx={{
                        justifyContent: 'flex-start',
                        mb: 0.75,
                        py: 1.25,
                        px: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        color: theme.palette.text.primary,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      OTC Products
                    </Button>

                    <Button
                      fullWidth
                      startIcon={<CardMembership sx={{ fontSize: 18 }} />}
                      onClick={() => { navigate('/membership'); setMobileMenuOpen(false); }}
                      sx={{
                        justifyContent: 'flex-start',
                        mb: 0.75,
                        py: 1.25,
                        px: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        color: theme.palette.text.primary,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      Membership
                    </Button>

                    {/* Mobile Beauty & Wellness Categories */}
                    <Box sx={{ 
                      mt: 1.5,
                      p: 1.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 1.5,
                      border: '1px solid rgba(0, 0, 0, 0.04)'
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontWeight: 600,
                          mb: 1.5,
                          display: 'block',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Categories
                      </Typography>
                      <Grid container spacing={1}>
                        {categories.map((item, index) => {
                          const IconComponent = getCategoryIcon(item.name);
                          return (
                            <Grid item xs={6} key={index}>
                              <Button
                                fullWidth
                                onClick={() => { handleOtcPage(item.name); setMobileMenuOpen(false); }}
                                sx={{ 
                                  justifyContent: 'flex-start', 
                                  fontSize: '0.75rem', 
                                  color: theme.palette.text.secondary,
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  py: 1,
                                  px: 1.5,
                                  minHeight: 'auto',
                                  borderRadius: 1.5,
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    backgroundColor: `${theme.palette.primary.main}12`,
                                    color: theme.palette.primary.main,
                                    transform: 'translateX(2px)'
                                  }
                                }}
                              >
                                <Box sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  width: '100%'
                                }}>
                                  <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    borderRadius: 1,
                                    backgroundColor: `${theme.palette.primary.main}15`,
                                    flexShrink: 0
                                  }}>
                                    <IconComponent 
                                      style={{ 
                                        fontSize: 14,
                                        color: theme.palette.primary.main
                                      }} 
                                    />
                                  </Box>
                                  <Typography 
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      lineHeight: 1.2,
                                      textAlign: 'left',
                                      flex: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {item.name}
                                  </Typography>
                                </Box>
                              </Button>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Box>

                  {/* User Actions */}
                  <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.08)' }} />
                  {!loginFlag ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Login sx={{ fontSize: 18 }} />}
                      onClick={() => { handleLoginClick(); setMobileMenuOpen(false); }}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        borderWidth: 1.5,
                        py: 1.25,
                        '&:hover': {
                          borderWidth: 1.5,
                        }
                      }}
                    >
                      Sign In / Sign Up
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.5 }}>
                      <Tooltip title={`Cart (${cartCount} items)`} arrow>
                        <IconButton 
                          onClick={() => { handleCartClick(); setMobileMenuOpen(false); }}
                          sx={{
                            p: 1.5,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <Badge badgeContent={cartCount} color="error" sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.7rem',
                              minWidth: 18,
                              height: 18,
                            }
                          }}>
                            <ShoppingCart sx={{ fontSize: 22 }} />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Profile" arrow>
                        <IconButton 
                          onClick={handleMenuClick}
                          sx={{
                            p: 1.5,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <AccountCircle sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Container>
          </Toolbar>
        </AppBar>
      </Box>

      {/* OtcToolbar - Always visible */}
      {/* <OtcToolbar /> */}
                  
      {/* Pincode Unavailable Dialog */}
      <Dialog
        open={showPincodeDialog}
        onClose={() => setShowPincodeDialog(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: `${theme.palette.primary.main}0.5`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'error.main',
          fontWeight: 600,
          textAlign: 'center'
        }}>
          Delivery Not Available
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            We don't deliver to <strong>{pincode}</strong> yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please try a different pincode.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button 
            onClick={() => {
              setShowPincodeDialog(false);
              setPincode('');
              setPincodeStatus(null);
              setPincodeMessage('');
            }}
            variant="contained"
            sx={{ textTransform: 'none', px: 4 }}
          >
            Try Different Pincode
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppBarMain;